/**
 * useOperatorState — composes OperatorState from three lanes:
 *   1. Plugin-owned snapshot  (bridge `app.bootstrap`)
 *   2. Gateway-owned settings (Redux / gateway transport)
 *   3. Browser-local preview  (localStorage)
 *
 * Spec: 10-decision-state-model-and-home-scope.md
 * Plan: 21-launch-implementation-plan.md
 *
 * The hook calls the bridge on mount, reads localStorage once, and
 * subscribes to Redux for gateway settings. All derivation rules live
 * in loader/composeOperatorState.js.
 */

import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ADMIN_RUNTIME_COMMANDS } from '../../../app/runtime/adminRuntime';
import {
  ConnectionStatus,
  IndexingStatus,
  StorefrontMode,
  PreviewStatus,
  LiveStatus,
  IntegrationHealth,
  DiagnosticsSeverity,
  EMPTY_OPERATOR_STATE,
} from '../operatorState';
import { computeNextAction } from '../computeNextAction';
import {
  degradedPluginLane,
  normalizeRuntimeOperatorLane,
} from '../loader/readPluginLane';
import { readBrowserLocalLane } from '../loader/readBrowserLocalLane';
import { readGatewayLane } from '../loader/readGatewayLane';
import { composeOperatorState } from '../loader/composeOperatorState';
import { OPERATOR_STATE_REFRESH_EVENT } from '../livePublishEvents';
import { useShellAppContext } from '../../shell/context/useShellAppContext';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOperatorState() {
  // --- Product-owned lane (runtime adapter, async) ---
  const [pluginLane, setPluginLane] = useState(null);
  const reduxShopId = useSelector(state => state.app?.currentShop ?? null);
  const { runtime, routeShopId, productProfile } = useShellAppContext();
  const shopId = routeShopId || reduxShopId || null;

  useEffect(() => {
    let cancelled = false;

    const refreshPluginLane = () => {
      if (!runtime || !shopId || !runtime.supports(ADMIN_RUNTIME_COMMANDS.OPERATOR_STATE)) {
        setPluginLane(degradedPluginLane(new Error('Operator state runtime is unavailable')));
        return;
      }
      runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.OPERATOR_STATE, { shopId })
        .then(normalizeRuntimeOperatorLane)
        .catch(degradedPluginLane)
        .then((result) => {
          if (!cancelled) setPluginLane(result);
        });
    };

    refreshPluginLane();

    if (typeof window !== 'undefined') {
      window.addEventListener(OPERATOR_STATE_REFRESH_EVENT, refreshPluginLane);
    }

    return () => {
      cancelled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener(OPERATOR_STATE_REFRESH_EVENT, refreshPluginLane);
      }
    };
  }, [runtime, shopId]);

  // --- Browser-local lane (sync, read once) ---
  const browserLocalLane = useMemo(() => readBrowserLocalLane(), []);

  // --- Gateway lane (Redux selectors) ---
  // Mode: read from persisted V2 config, not from wizard draft state.
  // V2 config is loaded on app init (Router.js dispatches loadSearchExperienceConfig).
  const isFetched = useSelector((state) => state.settings?.isFetched ?? false);
  // isFetching tracks an active in-flight request. When both are false, settings
  // were never requested (bridge-only / DevShell mode) — don't block on gateway.
  const isFetching = useSelector((state) => state.settings?.isFetching ?? false);
  const searchVisible = useSelector(
    (state) => state.settings?.searchVisible?.value ?? false,
  );
  const selectedExperience = useSelector(
    (state) => state.searchExperience?.config?.search?.searchExperience ?? null,
  );
  const gatewayLane = useMemo(
    () => readGatewayLane({ isFetched, selectedExperience, searchVisible }),
    [isFetched, selectedExperience, searchVisible],
  );

  // --- Compose ---
  const operatorState = useMemo(
    () => {
      if (!pluginLane) return EMPTY_OPERATOR_STATE;
      return composeOperatorState(pluginLane, browserLocalLane, gatewayLane);
    },
    [pluginLane, browserLocalLane, gatewayLane],
  );

  // Operator actions come from the plugin lane. Do not keep Home behind the
  // independent editor-config request while publish state is already ready.
  const waitsForLegacySettings = productProfile?.id !== 'plugin';
  const isLoading = !pluginLane
    || (waitsForLegacySettings && isFetching && !isFetched);

  return { operatorState, isLoading, pluginSnapshot: pluginLane, browserLocalLane };
}

// ---------------------------------------------------------------------------
// Legacy derivation — kept for backward compat with existing tests.
// The hook above no longer uses this. New code should use the loader.
// ---------------------------------------------------------------------------

const LEGACY_MODE_MAP = {
  unified: StorefrontMode.WIDGET,
  fullpage: StorefrontMode.COMBINED,
  classic: StorefrontMode.CLASSIC,
};

function legacyNormalizeMode(raw) {
  if (!raw) return null;
  return LEGACY_MODE_MAP[raw] || raw;
}

function legacyDiagnosticsSeverity(state) {
  if (
    state.connection === ConnectionStatus.ERROR
    || state.indexing === IndexingStatus.ERROR
    || state.preview === PreviewStatus.ERROR
    || state.live === LiveStatus.ERROR
    || state.integrationHealth === IntegrationHealth.ERROR
  ) {
    return DiagnosticsSeverity.ERROR;
  }
  if (state.integrationHealth === IntegrationHealth.WARNING) {
    return DiagnosticsSeverity.WARNING;
  }
  return DiagnosticsSeverity.NONE;
}

/** @deprecated Use composeOperatorState from loader/ instead */
export function deriveOperatorState({
  isFetched,
  searchVisible,
  wizardCompleted,
  wizardState,
}) {
  let connection;
  if (!isFetched) {
    connection = ConnectionStatus.NOT_CONNECTED;
  } else if (wizardCompleted) {
    connection = ConnectionStatus.CONNECTED;
  } else {
    connection = ConnectionStatus.NOT_CONNECTED;
  }

  const selectedExperience = wizardState?.steps?.find(
    (s) => s.id === 'experience-selection' || s.id === 'choose-experience',
  )?.data?.selectedExperience;
  const mode = legacyNormalizeMode(selectedExperience);

  let indexing;
  if (!isFetched) {
    indexing = IndexingStatus.IDLE;
  } else if (wizardCompleted) {
    indexing = IndexingStatus.READY;
  } else {
    indexing = IndexingStatus.IDLE;
  }

  const wizardStatus = wizardState?.status;
  let preview;
  if (wizardStatus === 'completed') {
    preview = PreviewStatus.READY;
  } else {
    preview = PreviewStatus.NOT_READY;
  }

  let live;
  if (searchVisible && connection === ConnectionStatus.CONNECTED) {
    live = LiveStatus.ACTIVE;
  } else if (searchVisible && connection === ConnectionStatus.ERROR) {
    live = LiveStatus.ERROR;
  } else {
    live = LiveStatus.NOT_ACTIVE;
  }

  const integrationHealth = IntegrationHealth.UNKNOWN;

  const partial = {
    connection,
    indexing,
    mode,
    preview,
    live,
    integrationHealth,
    diagnosticsSeverity: DiagnosticsSeverity.NONE,
    nextAction: null,
  };

  partial.diagnosticsSeverity = legacyDiagnosticsSeverity(partial);
  partial.nextAction = computeNextAction(partial);

  return partial;
}

// ---------------------------------------------------------------------------
// Documented data gaps — now mostly resolved by the 3-lane loader.
// Kept for backward compat with existing test imports.
// ---------------------------------------------------------------------------

export const DATA_GAPS = Object.freeze([
  {
    field: 'connection',
    gap: 'Legacy: derived from settings.isFetched. Loader: from bridge auth.',
    blocker: false,
    resolution: 'Resolved by readPluginLane (app.bootstrap auth.connected + auth.has_token).',
  },
  {
    field: 'indexing',
    gap: 'Legacy: proxy from wizardCompleted. Loader: from bridge indexing.status.',
    blocker: false,
    resolution: 'Resolved by readPluginLane (app.bootstrap indexing.status).',
  },
  {
    field: 'preview',
    gap: 'No build status tracking. Inferred from wizard completion status.',
    blocker: false,
    resolution: 'Track preview build status (building/ready/error) via bridge or backend polling.',
  },
  {
    field: 'integrationHealth',
    gap: 'Legacy: always unknown. Loader: derived from preview feedback.',
    blocker: false,
    resolution: 'Resolved by readBrowserLocalLane (preview feedback health checks).',
  },
  {
    field: 'diagnosticsSeverity',
    gap: 'Derived locally from other fields. No backend diagnostics subsystem.',
    blocker: false,
    resolution: 'Build diagnostics aggregation when Diagnostics screen is implemented.',
  },
]);
